import React, { ReactNode, useEffect, useState } from 'react';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import Divider from '../../../components/Divider';
import FormInput from '../../../components/FormInput';
import { WindowInformation } from '../../../services/window-information';
import Tooltip from '../../../components/Tooltip';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import WorkplacesDropdown from '../../../components/deac/WorkplacesDropdown';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { deacService } from '../../../services/central-api/deac';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import UnitProgramDropdown from '../../../components/deac/UnitProgramDropdown';
import VacancyLevelsInputs from '../Vacancies/VacancyLevelsInputs';
import VacancyRequirementsInputs from '../Vacancies/VacancyRequirementsInputs';
import VacancyTagsInputs from '../Vacancies/VacancyTagsInputs';
import DeleteVacancyTemplateModal from './DeleteVacancyTemplateModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { useQuery } from '@tanstack/react-query';
import { DeacParamList } from '../../../typings/Params';
import { RootNavigation } from '../../../Routes';

interface SectionHeaderParams {
    title: string;
    endContent?: ReactNode;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
    required?: boolean;
}

function SectionHeader({ children, endContent, title, viewZIndex, childrenZIndex, required }: SectionHeaderParams) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
            <View style={{ flexDirection: 'row', columnGap: 8, alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={styles.sectionHeaderTitle}>{translate(title)}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                {endContent ? endContent : null}
            </View>
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, gap: 4 }}>
            {children}
        </View>
    </View>;
}

export default function VacancyTemplateForm({ navigation }: {
    navigation: RootNavigation;
}) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DeacParamList, 'VacancyTemplateForm'>>();
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [template, setTemplate] = useState<VacancyTemplate | null>(null);

    const [templateName, setTemplateName] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(0);
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(0);
    const [address, setAddress] = useState<string>('');
    const [workplaceId, setWorkplaceId] = useState<number>(0);
    const [unitProgramId, setUnitProgramId] = useState<number>(0);
    const [levelsIds, setLevelsIds] = useState<number[]>([]);
    const [requirementsIds, setRequirementsIds] = useState<number[]>([]);
    const [tagsIds, setTagsIds] = useState<number[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const IsEditing = route.params?.id ? true : false;

    function cleanValues() {
        setTemplate(null);
        setTemplateName('');
        setTitle('');
        setDescription('');
        setQuantity(0);
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setAddress('');
        setWorkplaceId(0);
        setUnitProgramId(0);
        setLevelsIds([]);
        setRequirementsIds([]);
        setTagsIds([]);
    }

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacancyTemplateForm', route.params?.id],
        queryFn: () => deacService.getVacancyTemplate(route.params?.id || 0),
        enabled: !!authenticatedUser && isFocused && !!route.params && !!route.params.id,
    });

    const isEnabled = !!authenticatedUser && isFocused && !!route.params && !!route.params.id;
    const isFetchingContent = isEnabled && queryResult.isFetching;

    const { data, isError, error } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }

        setTemplate(data);
        setTemplateName(data.templateName);
        setTitle(data.title);
        setDescription(data.description);
        setQuantity(data.quantity);
        setActingBodyId(data.workplace.actingBodyUnit.actingBody.id);
        setActingBodyUnitId(data.workplace.actingBodyUnit.id);
        setActingBodyCommandId(data.workplace.actingBodyCommand.id);
        setAddress(data.workplace.address);
        setWorkplaceId(data.workplace.id);
        setUnitProgramId(data.unitProgram?.id ?? 0);
        setLevelsIds(data.levels?.map(l => l.id) ?? []);
        setRequirementsIds(data.skills?.map(s => s.id) ?? []);
        setTagsIds(data.tags?.map(t => t.id) ?? []);
    }, [data]);

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    async function formSubmit() {
        try {
            setIsLoading(true);
            if (route.params?.id) {
                await deacService.updateVacancyTemplate({
                    id: route.params.id,
                    templateName: templateName,
                    title: title,
                    description: description,
                    quantity: quantity,
                    levelsIds: levelsIds,
                    skillsIds: requirementsIds,
                    tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
                    workplaceId: workplaceId,
                    unitProgramId: unitProgramId,
                });
            } else {
                await deacService.createVacancyTemplate({
                    templateName: templateName,
                    title: title,
                    description: description,
                    quantity: quantity,
                    levelsIds: levelsIds,
                    skillsIds: requirementsIds,
                    tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
                    workplaceId: workplaceId,
                    unitProgramId: unitProgramId,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            navigation.navigate('Deac', { screen: 'VacanciesTemplates' });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function isFormValid(): boolean {
        if (templateName == '' ||
            title == '' ||
            description == '' ||
            workplaceId == 0 ||
            unitProgramId == 0 ||
            quantity == 0 ||
            levelsIds.length == 0 ||
            requirementsIds.length == 0
        ) {
            return false;
        }

        return true;
    }

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies_templates) {
            return actingBodyId;
        }
        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    function getActingBodyUnitId() {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
            authenticatedUser.permissions.deac_manage_acting_body_units_vacancies_templates
        ) {
            return actingBodyUnitId;
        }
        return authenticatedUser.actingBodyUnitId;
    }

    return (
        <Container>
            <Header
                title={IsEditing ? translate('viewVacancyTemplate') : translate('registerVacancyTemplate')}
                goBackAction={() => {
                    cleanValues();
                    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Deac', { screen: 'VacanciesTemplates' });
                }}
                actions={<View style={{ flexDirection: 'row', gap: 8 }}>
                    {(IsEditing)
                        ? <>
                            <CustomButton
                                text={translate('delete')}
                                onPress={() => setIsDeleteModalVisible(true)}
                                type='tertiary'
                            />
                        </>
                        : null}
                    <CustomButton
                        text={IsEditing ? translate('goBack') : translate('cancel')}
                        onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Deac', { screen: 'VacanciesTemplates' })}
                        type='secondary'
                    />
                    <CustomButton text={translate('save')} onPress={formSubmit} disabled={!isFormValid()} isLoading={isLoading} />
                </View>}
            />
            <Card>
                {isFetchingContent
                    ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                    </View>
                    :
                    <ScrollView contentContainerStyle={{ flexDirection: 'column', padding: 4, rowGap: 24 }}>
                        <SectionHeader title='templateName'>
                            <FormInput
                                value={templateName}
                                onChangeText={setTemplateName}
                                required
                                label={translate('templateName')}
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                            />
                        </SectionHeader>

                        <SectionHeader title='activityTitle'>
                            <FormInput
                                value={title}
                                onChangeText={setTitle}
                                required
                                label={translate('activityTitle')}
                                labelStyle={styles.fieldsLabelStyle}
                                maxCharacters={50}
                                viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                            />
                        </SectionHeader>

                        <SectionHeader title='activityDescription'>
                            <FormInput
                                value={description}
                                onChangeText={setDescription}
                                required
                                label={translate('description')}
                                labelStyle={styles.fieldsLabelStyle}
                                maxCharacters={140}
                                numberOfLines={3}
                                multiline
                                viewStyle={[{ gap: 8 }]}
                            />
                        </SectionHeader>

                        <SectionHeader viewZIndex={8} title='correspondentWorkplace'>
                            <View style={{ flexDirection: 'column', gap: 16 }}>
                                <View style={{ zIndex: 2, flexDirection: windowInfo.isMobile ? 'column' : 'row', flex: 1, flexWrap: 'wrap', gap: 16 }}>
                                    <ActingBodiesDropdown
                                        value={getActingBodyId()}
                                        setValue={(value) => {
                                            setActingBodyId(value);

                                            setActingBodyUnitId(0);
                                            setWorkplaceId(0);
                                            setActingBodyCommandId(0);
                                            setAddress('');
                                        }}
                                        labelStyle={styles.fieldsLabelStyle}
                                        zIndex={5}
                                        includeAllOption={false}
                                        required
                                        disabled={!authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies_templates}
                                        viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                    />
                                    <ActingBodiesUnitsDropdown
                                        value={getActingBodyUnitId()}
                                        setValue={(value) => {
                                            setActingBodyUnitId(value);

                                            setWorkplaceId(0);
                                            setActingBodyCommandId(0);
                                            setAddress('');
                                        }}
                                        actingBodyId={getActingBodyId()}
                                        labelStyle={styles.fieldsLabelStyle}
                                        zIndex={4}
                                        required
                                        disabled={(!authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies_templates &&
                                            !authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies_templates)
                                        }
                                        viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                    />
                                    <WorkplacesDropdown
                                        value={workplaceId}
                                        setValue={setWorkplaceId}
                                        zIndex={3}
                                        disable={!getActingBodyId() || !getActingBodyUnitId()}
                                        actingBodyId={getActingBodyId()}
                                        actingBodyUnitId={getActingBodyUnitId()}
                                        setUnit={setActingBodyUnitId}
                                        setCommand={setActingBodyCommandId}
                                        setAddress={setAddress}
                                        required
                                        labelStyle={styles.fieldsLabelStyle}
                                        viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                    />
                                    <ActingBodiesCommandsDropdown
                                        actingBodyId={getActingBodyId()}
                                        disabled={true}
                                        value={actingBodyCommandId}
                                        setValue={setActingBodyCommandId}
                                        labelStyle={styles.fieldsLabelStyle}
                                        zIndex={2}
                                        viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                    />
                                </View>
                                <View style={{ zIndex: 1 }}>
                                    <FormInput
                                        value={address}
                                        onChangeText={setAddress}
                                        label={translate('location')}
                                        labelStyle={styles.fieldsLabelStyle}
                                        disabled
                                    />
                                </View>
                            </View>
                        </SectionHeader>

                        <SectionHeader viewZIndex={7} title='linkedProgram'>
                            <UnitProgramDropdown
                                actingBodyId={getActingBodyId()}
                                value={unitProgramId}
                                setValue={setUnitProgramId}
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                required
                            />
                        </SectionHeader>

                        <SectionHeader viewZIndex={6} title='vacanciesOffered'>
                            <FormInput
                                value={quantity.toString()}
                                onChangeText={(val) => setQuantity(Number(val))}
                                required
                                label={translate('quantity')}
                                mode='NUMBER'
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '20%' }]}
                            />
                        </SectionHeader>

                        <SectionHeader
                            viewZIndex={5}
                            title='vacancyLevel'
                            required
                            endContent={
                                <Tooltip
                                    useSimpleBox
                                    style={{
                                        color: theme.colors.white['500'],
                                        padding: 16,
                                        borderRadius: 12,
                                        justifyContent: 'center',
                                        zIndex: 9999,
                                        background: theme.colors.neutral['500'],
                                    }}
                                    tooltipContent={translate('levelRegisteredWithoutRoleAssociated')}
                                    tooltipDirection='right'
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                                </Tooltip>
                            }
                        >
                            <VacancyLevelsInputs
                                actingBodyId={getActingBodyId()}
                                value={levelsIds}
                                setValue={setLevelsIds}
                            />
                        </SectionHeader>

                        <SectionHeader viewZIndex={4} title='requirements' required>
                            <VacancyRequirementsInputs
                                actingBodyId={getActingBodyId()}
                                value={requirementsIds}
                                setValue={setRequirementsIds}
                            />
                        </SectionHeader>

                        <SectionHeader title='tags' viewZIndex={1}>
                            <VacancyTagsInputs
                                value={tagsIds}
                                setValue={setTagsIds}
                            />
                        </SectionHeader>
                    </ScrollView>
                }
            </Card>
            {template && <DeleteVacancyTemplateModal
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
                selectedTemplate={template}
                onConfirm={() => navigation.navigate('Deac', { screen: 'VacanciesTemplates' })}
            />}
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
}));
